import React, { useEffect, useRef, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { ChevronDown, Check, X } from "lucide-react";
import { usePromptFragments } from "../hooks";

interface IPromptFragmentsMultiSelect {
  form: UseFormReturn<any, any>;
  name?: string;
  label?: string;
}

export const PromptFragmentsMultiSelect: React.FC<IPromptFragmentsMultiSelect> = ({
  form,
  name = "promptFragmentIds",
  label = "Fragments de style",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpened, setIsOpened] = useState(false);
  const { data } = usePromptFragments();

  const selectedIds: number[] = form.watch(name) ?? [];
  const fragments = data?.promptFragments?.results ?? [];

  const toggleFragment = (fragmentId: number) => {
    const next = selectedIds.includes(fragmentId)
      ? selectedIds.filter((id) => id !== fragmentId)
      : [...selectedIds, fragmentId];
    form.setValue(name, next);
  };

  const removeFragment = (fragmentId: number) => {
    form.setValue(
      name,
      selectedIds.filter((id) => id !== fragmentId)
    );
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpened(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedFragments = fragments.filter((f) => selectedIds.includes(f.id));

  return (
    <div className="flex flex-col w-full relative" ref={containerRef}>
      <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">
        {label}
      </label>

      <button
        type="button"
        onClick={() => setIsOpened((prev) => !prev)}
        className="w-full min-h-[42px] px-3 py-2 border rounded-md text-sm text-left bg-white dark:bg-surface-dark-alt border-gray-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-primary-500 focus:ring-primary-500 transition-colors duration-200 flex items-center justify-between gap-2"
      >
        <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
          {selectedFragments.length === 0 ? (
            <span className="text-gray-400 dark:text-zinc-500">Aucun fragment sélectionné</span>
          ) : (
            selectedFragments.map((fragment) => (
              <span
                key={fragment.id}
                className="inline-flex items-center gap-1 bg-primary-50 dark:bg-primary-950/50 text-primary-700 dark:text-primary-300 text-xs font-medium px-2 py-0.5 rounded-full"
              >
                {fragment.name}
                <span
                  role="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFragment(fragment.id);
                  }}
                  className="hover:text-primary-900 dark:hover:text-primary-100"
                >
                  <X size={12} />
                </span>
              </span>
            ))
          )}
        </div>
        <ChevronDown
          size={16}
          className={`flex-shrink-0 text-gray-400 transition-transform ${
            isOpened ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpened && (
        <div className="absolute z-10 top-full mt-1 w-full bg-white dark:bg-surface-dark-alt border border-gray-200 dark:border-zinc-800 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {fragments.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-zinc-400">
              Aucun fragment disponible
            </div>
          ) : (
            fragments.map((fragment) => {
              const isSelected = selectedIds.includes(fragment.id);
              return (
                <div
                  key={fragment.id}
                  onClick={() => toggleFragment(fragment.id)}
                  className="flex items-center justify-between gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-zinc-800"
                >
                  <span className="text-gray-900 dark:text-zinc-100">{fragment.name}</span>
                  {isSelected && <Check size={14} className="text-primary-600 flex-shrink-0" />}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};
